/**
 * Results Filters Hook
 *
 * Manages filter state for the results page sidebar.
 * Converts UI selections to API query parameters.
 */
import { useCallback, useState } from "react";

import { isHiddenAdvancedAttributeKey } from "@/lib/filter-visibility";
import { callerFacet } from "@/lib/facet-provenance";
import type { Facet } from "openapi/shop_agent/client/types.gen";

export const PRICE_RANGE_MIN_DEFAULT = 0;
export const PRICE_RANGE_MAX_DEFAULT = 1200;

export interface CategoryNode {
  name: string;
  path: string;
  children?: CategoryNode[];
  count?: number;
}

export interface FiltersState {
  categories: string[];
  subcategories: string[];
  priceRange: [number, number];
  colors: string[];
  brands: string[];
  similarToBrands: string[]; // Brands for style-based similarity search
  advanced: Record<string, string[]>;
  categoryNavigationStack: CategoryNode[]; // Track drill-down state
}

export interface UseResultsFiltersReturn {
  filters: FiltersState;
  updateCategories: (categories: string[]) => void;
  updateSubcategories: (subcategories: string[]) => void;
  updatePriceRange: (range: [number, number]) => void;
  updateColors: (colors: string[]) => void;
  updateBrands: (brands: string[]) => void;
  updateSimilarToBrands: (brands: string[]) => void;
  updateAdvanced: (advanced: Record<string, string[]>) => void;
  handleAdvancedAttributeToggle: (attributeHandle: string, value: string) => void;
  updateCategoryNavigationStack: (stack: CategoryNode[]) => void;
  replaceFilters: (newFilters: FiltersState) => void;
  clearAllFilters: () => void;
  getAppliedFiltersCount: () => number;
}

const DEFAULT_FILTERS: FiltersState = {
  categories: [],
  subcategories: [],
  priceRange: [PRICE_RANGE_MIN_DEFAULT, PRICE_RANGE_MAX_DEFAULT],
  colors: [],
  brands: [],
  similarToBrands: [],
  advanced: {},
  categoryNavigationStack: [],
};

function shouldCountAdvancedFilter(attributeKey: string): boolean {
  return !(
    attributeKey.startsWith("category_path.depth_") || isHiddenAdvancedAttributeKey(attributeKey)
  );
}

/**
 * Counts only visible, user-facing filters for the sidebar/header badges.
 *
 * Intentionally excludes category navigation state and hidden/system facets
 * such as gender and age groups, because those are not rendered as applied
 * filters in the left sidebar.
 */
export function getAppliedVisibleFiltersCount(filters: FiltersState): number {
  let count = 0;
  count += filters.colors.length;
  count += filters.brands.length;
  count += filters.similarToBrands.length;

  // Count price filter as 1 if not default.
  if (
    filters.priceRange[0] !== PRICE_RANGE_MIN_DEFAULT ||
    filters.priceRange[1] !== PRICE_RANGE_MAX_DEFAULT
  ) {
    count += 1;
  }

  // Only count attribute filters that are surfaced as visible user-facing filters.
  Object.entries(filters.advanced).forEach(([attributeKey, values]) => {
    if (!shouldCountAdvancedFilter(attributeKey)) {
      return;
    }
    count += values.length;
  });

  return count;
}

export function useResultsFilters(initialFilters?: Partial<FiltersState>): UseResultsFiltersReturn {
  const [filters, setFilters] = useState<FiltersState>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });

  const updateCategories = useCallback((categories: string[]) => {
    setFilters((prev) => ({ ...prev, categories }));
  }, []);

  const updateSubcategories = useCallback((subcategories: string[]) => {
    setFilters((prev) => ({ ...prev, subcategories }));
  }, []);

  const updatePriceRange = useCallback((priceRange: [number, number]) => {
    setFilters((prev) => ({ ...prev, priceRange }));
  }, []);

  const updateColors = useCallback((colors: string[]) => {
    setFilters((prev) => ({ ...prev, colors }));
  }, []);

  const updateBrands = useCallback((brands: string[]) => {
    setFilters((prev) => ({ ...prev, brands }));
  }, []);

  const updateSimilarToBrands = useCallback((similarToBrands: string[]) => {
    setFilters((prev) => ({ ...prev, similarToBrands }));
  }, []);

  const updateAdvanced = useCallback((advanced: Record<string, string[]>) => {
    setFilters((prev) => ({ ...prev, advanced }));
  }, []);

  const handleAdvancedAttributeToggle = useCallback((attributeHandle: string, value: string) => {
    setFilters((prev) => {
      const newAdvanced = { ...prev.advanced };
      const currentValues = newAdvanced[attributeHandle] || [];

      if (currentValues.includes(value)) {
        // Remove value
        newAdvanced[attributeHandle] = currentValues.filter((v) => v !== value);
        if (newAdvanced[attributeHandle].length === 0) {
          delete newAdvanced[attributeHandle];
        }
      } else {
        // Add value
        newAdvanced[attributeHandle] = [...currentValues, value];
      }

      return { ...prev, advanced: newAdvanced };
    });
  }, []);

  const updateCategoryNavigationStack = useCallback((stack: CategoryNode[]) => {
    setFilters((prev) => ({ ...prev, categoryNavigationStack: stack }));
  }, []);

  const replaceFilters = useCallback((newFilters: FiltersState) => {
    setFilters(newFilters);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const getAppliedFiltersCount = useCallback(() => {
    return getAppliedVisibleFiltersCount(filters);
  }, [filters]);

  return {
    filters,
    updateCategories,
    updateSubcategories,
    updatePriceRange,
    updateColors,
    updateBrands,
    updateSimilarToBrands,
    updateAdvanced,
    handleAdvancedAttributeToggle,
    updateCategoryNavigationStack,
    replaceFilters,
    clearAllFilters,
    getAppliedFiltersCount,
  };
}

/**
 * Convert filter state to API facets format
 */
export function filtersToFacets(filters: FiltersState): Facet[] {
  // Every facet here is a filter the shopper set: stamped so relaxation
  // never drops it to recover a zero-result search.
  const facets: Facet[] = [];

  if (filters.categories.length > 0) {
    facets.push(callerFacet("category", filters.categories));
  }

  if (filters.subcategories.length > 0) {
    facets.push(callerFacet("subcategory", filters.subcategories));
  }

  if (filters.colors.length > 0) {
    facets.push(callerFacet("color", filters.colors));
  }

  if (filters.brands.length > 0) {
    facets.push(callerFacet("brand", filters.brands));
  }

  if (
    filters.priceRange[0] !== PRICE_RANGE_MIN_DEFAULT ||
    filters.priceRange[1] !== PRICE_RANGE_MAX_DEFAULT
  ) {
    facets.push(callerFacet("price", [`${filters.priceRange[0]}-${filters.priceRange[1]}`]));
  }

  // Add advanced filters — the shopper's too.
  Object.entries(filters.advanced).forEach(([key, values]) => {
    if (values.length > 0) {
      facets.push(callerFacet(key, values));
    }
  });

  return facets;
}
